// SealedUsbHub.js — v6.43.4 Sealed MOSAIC USB · Super Admin one-door hub
// =====================================================================
// Super-admin only. Consolidates every artifact + workflow that touches
// a sealed drive so ops doesn't have to hunt across seven routes:
//
//   1. Latest QC drive card       → auto-selects most recent minted drive,
//                                    generates 30-day signed URLs on demand.
//   2. Mint history table         → every drive currently staged on disk
//                                    with size/build/artifact status.
//   3. Flash guides per OS        → macOS / Windows / Chromebook / Linux
//                                    step-by-step (no download hunting).
//   4. Retail insert + collateral → direct download to the printed
//                                    box-insert PDF, procurement letter,
//                                    pricing flyer.
//   5. Compatibility matrix       → link to the live-probe page (I30).
//
// Backend endpoints used:
//   GET /api/qc-usb/list                     (drive enumeration)
//   GET /api/qc-usb/{serial}/mint-links      (30-day signed URLs)
//   GET /api/qc-usb/{serial}/retail-insert   (attached download)
//   GET /api/qc-usb/{serial}/download        (tarball)
//   GET /api/qc-usb/{serial}/flashable-img   (raw .img.gz)
//
// Design intent: single-page-scroll. No tabs. Sections stack top-down
// in the order a rep actually walks: "grab the drive → know how to
// flash it → know what device it runs on → know what to hand the
// procurement team."
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import UsbOrdersPanel from "../components/UsbOrdersPanel";

const API = process.env.REACT_APP_BACKEND_URL;

// Axios interceptor (registered by AuthContext) auto-attaches the
// Bearer header from localStorage['mosaic_session_token'] on every
// request — no manual authHeader() needed here.

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};
const fmtWhen = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts * 1000).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};
const absUrl = (path) => (path.startsWith("http") ? path : `${API}${path}`);

/* v6.43.8 — Password-gate pill. Shows whether a drive is FERPA-locked
   at rest (AES-256-GCM PII envelope) or plaintext (QC / demo). Hover
   reveals the fingerprint so ops can cross-check the printed sticker. */
const PasswordPill = ({ gated, fingerprint, testId }) => {
  if (!gated) {
    return (
      <span
        data-testid={testId}
        title="Drive is not password-gated (demo / QC data only)"
        style={{
          display: "inline-block",
          padding: "1px 7px",
          background: "#F1F5F9",
          color: "#64748B",
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".03em",
        }}
      >
        ○ Open
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      title={`AES-256-GCM sealed · fingerprint ${fingerprint || "—"}`}
      style={{
        display: "inline-block",
        padding: "1px 7px",
        background: "#EEF2FF",
        color: "#4338CA",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: ".03em",
      }}
    >
      🔒 Gated{fingerprint ? ` · ${fingerprint}` : ""}
    </span>
  );
};

/* v6.43.5 — verifier pill. Reads the sidecar report shipped alongside
   every drive and shows a green (all 30 passing), amber (some failing),
   or gray (never verified) chip. Ops eyeballs this before shipping. */
const VerifierPill = ({ v, size = "md", testId }) => {  const pad = size === "sm" ? "1px 7px" : "3px 10px";
  const fs = size === "sm" ? 10 : 11;
  if (!v) {
    return (
      <span
        data-testid={testId}
        style={{
          display: "inline-block",
          padding: pad,
          background: "#F1F5F9",
          color: "#64748B",
          borderRadius: 999,
          fontSize: fs,
          fontWeight: 700,
          letterSpacing: ".03em",
        }}
      >
        ○ Not verified
      </span>
    );
  }
  if (v.all_passed) {
    return (
      <span
        data-testid={testId}
        title={`Last verified ${fmtWhen(v.verified_utc)}`}
        style={{
          display: "inline-block",
          padding: pad,
          background: "#E6F4EC",
          color: "#14804A",
          borderRadius: 999,
          fontSize: fs,
          fontWeight: 700,
          letterSpacing: ".03em",
        }}
      >
        ✓ {v.passed}/{v.total}
      </span>
    );
  }
  const failing = (v.failing_checks || []).slice(0, 3)
    .map((c) => c.id).join(", ");
  return (
    <span
      data-testid={testId}
      title={`${v.failed} failing: ${failing}${v.failing_checks?.length > 3 ? "…" : ""} · ${fmtWhen(v.verified_utc)}`}
      style={{
        display: "inline-block",
        padding: pad,
        background: "#FEE4E2",
        color: "#B42318",
        borderRadius: 999,
        fontSize: fs,
        fontWeight: 700,
        letterSpacing: ".03em",
      }}
    >
      ✕ {v.passed}/{v.total} · {v.failed} fail
    </span>
  );
};

// ─── Flash guides (per OS) ──────────────────────────────────────────
// Each guide is a short, verified sequence — no filler, no external
// links to hunt down. Kept inline so the hub works offline (ops laptop
// might be tethering during a demo).
const FLASH_GUIDES = [
  {
    os: "macOS",
    icon: "🍎",
    tint: "#F0F0F5",
    tool: "Terminal (built-in)  ·  or BalenaEtcher (GUI)",
    steps: [
      "Download the .img.gz to Downloads.",
      "Open Terminal → cd ~/Downloads",
      "gunzip mosaic.img.gz  → produces mosaic.img",
      "Plug in your target USB (WILL BE ERASED).",
      "diskutil list → find the target (e.g., /dev/disk4)",
      "sudo dd if=mosaic.img of=/dev/rdisk4 bs=4m status=progress",
      "Wait for done → drive auto-remounts as MOSAIC.",
    ],
    warning: "Double-check the disk number in diskutil — writing to the wrong disk wipes it. rdiskN (raw) is ~4× faster than diskN.",
  },
  {
    os: "Windows",
    icon: "🪟",
    tint: "#E8F0FE",
    tool: "BalenaEtcher (recommended)  ·  or Rufus",
    steps: [
      "Install BalenaEtcher from balena.io/etcher (free).",
      "Plug in your target USB (WILL BE ERASED).",
      "Open Etcher → Flash from file → pick the .img.gz.",
      "Select target → Flash!",
      "Wait for done → Windows may prompt \"format this drive\" — ignore that; it's already formatted.",
    ],
    warning: "Etcher handles .img.gz natively — no need to gunzip first. Antivirus may flash a warning; it's a false positive on the raw image.",
  },
  {
    os: "Chromebook",
    icon: "🌐",
    tint: "#E8F5E9",
    tool: "Chromebook Recovery Utility",
    steps: [
      "Install the Chromebook Recovery Utility from the Chrome Web Store.",
      "Open it → gear icon (⚙) → \"Use local image\".",
      "Select your MOSAIC .img.gz file.",
      "Insert target USB → pick it → Continue.",
      "Confirm erase → wait for done.",
    ],
    warning: "The Chromebook Recovery Utility is the only supported flasher on Chrome OS — BalenaEtcher's Chrome app was deprecated in 2023.",
  },
  {
    os: "Linux",
    icon: "🐧",
    tint: "#FFF3E0",
    tool: "dd  ·  or BalenaEtcher AppImage",
    steps: [
      "gunzip mosaic.img.gz",
      "lsblk → find the target (e.g., /dev/sdb).",
      "sudo umount /dev/sdb* (if auto-mounted).",
      "sudo dd if=mosaic.img of=/dev/sdb bs=4M status=progress conv=fsync",
      "sync && sudo eject /dev/sdb",
    ],
    warning: "Ubuntu / Fedora / Arch all work the same. Never write to /dev/sda (that's usually your system disk).",
  },
];

const HubSection = ({ title, subtitle, children, testId }) => (
  <section
    data-testid={testId}
    style={{
      background: "#FFFFFF",
      border: "1px solid #E4E7EC",
      borderRadius: 12,
      padding: "26px 28px",
      marginBottom: 20,
      boxShadow: "0 1px 2px rgba(15,23,42,.03)",
    }}
  >
    <h2
      style={{
        margin: 0,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: ".1em",
        textTransform: "uppercase",
        color: "#475569",
      }}
    >
      {title}
    </h2>
    {subtitle && (
      <p style={{ margin: "6px 0 20px", fontSize: 13, color: "#64748B", lineHeight: 1.5 }}>
        {subtitle}
      </p>
    )}
    {children}
  </section>
);

// ─── Signed-link box ────────────────────────────────────────────────
const SignedLinkRow = ({ label, href, filename, size, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(absUrl(href));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        border: "1px solid #E4E7EC",
        borderRadius: 8,
        background: "#FAFBFC",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{label}</div>
        <div
          style={{
            fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
            fontSize: 11,
            color: "#64748B",
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {filename} {size && `· ${size}`}
        </div>
      </div>
      <button
        type="button"
        onClick={copy}
        data-testid={`${testId}-copy`}
        style={{
          padding: "7px 12px",
          background: copied ? "#14804A" : "#FFFFFF",
          color: copied ? "#FFFFFF" : "#0F172A",
          border: "1px solid #CBD5E1",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {copied ? "✓ Copied" : "Copy link"}
      </button>
      <a
        href={absUrl(href)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testId}-open`}
        style={{
          padding: "7px 12px",
          background: "#1D4ED8",
          color: "#FFFFFF",
          border: "1px solid #1D4ED8",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          textDecoration: "none",
        }}
      >
        Download ↓
      </a>
    </div>
  );
};

/* v6.43.10 — Mint With Password panel.
   Ops fills in serial + district + school + band + grades + password
   (or "Open" for QC), submits, then the panel polls /mint-jobs/{id}
   every 2s until the build finishes. Reuses the existing 47-pytest +
   36-invariant safety net — the frontend is a thin form + poller. */
const StrengthMeter = ({ score, reasons }) => {
  const clamped = Math.max(0, Math.min(100, score || 0));
  const color = clamped >= 70 ? "#14804A"
              : clamped >= 40 ? "#B54708"
              : "#B42318";
  const label = clamped >= 70 ? "Strong"
              : clamped >= 40 ? "Fair"
              : "Weak";
  return (
    <div data-testid="mint-strength-meter" style={{ marginTop: 6 }}>
      <div style={{
        height: 6, background: "#E4E7EC", borderRadius: 4,
        overflow: "hidden",
      }}>
        <div style={{
          width: `${clamped}%`, height: "100%", background: color,
          transition: "width .2s ease, background .2s ease",
        }} />
      </div>
      <div style={{
        marginTop: 4, fontSize: 11, color: "#475569",
        display: "flex", justifyContent: "space-between",
      }}>
        <span>Strength · <b style={{ color }}>{label}</b></span>
        <span>{clamped}/100</span>
      </div>
      {reasons && reasons.length > 0 && (
        <ul style={{
          margin: "4px 0 0", paddingLeft: 18, fontSize: 11,
          color: "#B42318",
        }}>
          {reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
    </div>
  );
};

const MintFieldLabel = ({ children, hint }) => (
  <label style={{
    display: "block", fontSize: 11, fontWeight: 700,
    letterSpacing: ".05em", textTransform: "uppercase",
    color: "#475569", marginBottom: 4,
  }}>
    {children}
    {hint && (
      <span style={{
        marginLeft: 6, fontWeight: 400, textTransform: "none",
        color: "#94A3B8", letterSpacing: 0,
      }}>
        {hint}
      </span>
    )}
  </label>
);

const MintInputStyle = {
  width: "100%",
  padding: "9px 11px",
  border: "1px solid #CBD5E1",
  borderRadius: 6,
  fontSize: 13,
  color: "#0F172A",
  background: "#FFFFFF",
  boxSizing: "border-box",
};

const MintDrivePanel = ({ onMinted }) => {
  const [open, setOpen] = useState(false);
  const [serial, setSerial] = useState("");
  const [district, setDistrict] = useState("");
  const [school, setSchool] = useState("");
  const [band, setBand] = useState("K-8");
  const [grades, setGrades] = useState("K,1,2,3,4,5,6,7,8");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [strict, setStrict] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [job, setJob] = useState(null);

  // Live policy check as the operator types (debounced).
  useEffect(() => {
    if (!password) { setPolicy(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API}/api/qc-usb/password/check`, { password }
        );
        if (!cancelled) setPolicy(data);
      } catch { /* ignore transient errors */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [password]);

  const suggestPassword = async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/qc-usb/password/suggest?length=16`
      );
      setPassword(data.password);
      setShowPw(true);
    } catch (e) {
      setSubmitErr(e?.response?.data?.detail || e.message);
    }
  };

  // Prefill grades when band changes so ops doesn't have to re-type.
  // Also nudge the serial to a matching band prefix if the field is
  // empty (or still on its default) — otherwise leave what ops typed.
  const applyBand = (b) => {
    setBand(b);
    const gradesPreset = {
      "K-2":  "K,1,2",
      "K-5":  "K,1,2,3,4,5",
      "3-5":  "3,4,5",
      "K-8":  "K,1,2,3,4,5,6,7,8",
      "K-12": "K,1,2,3,4,5,6,7,8,9,10,11,12",
      "6-8":  "6,7,8",
      "9-12": "9,10,11,12",
    }[b];
    if (gradesPreset) setGrades(gradesPreset);
    const bandCode = {
      "K-2":  "K2", "K-5":  "K5", "3-5":  "35",
      "K-8":  "K8", "K-12": "K12", "6-8":  "68", "9-12": "912",
    }[b];
    if (bandCode) {
      // If serial is empty, or still points at a different band, rewrite
      // the band segment. Preserves any suffix ops has already typed.
      setSerial((prev) => {
        const stub = `MOS-USB-${bandCode}-QC001`;
        if (!prev) return stub;
        const m = prev.match(/^MOS-USB-([A-Z0-9]+)-(.*)$/i);
        if (!m) return stub;
        if (m[1].toUpperCase() === bandCode) return prev;
        return `MOS-USB-${bandCode}-${m[2]}`;
      });
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitErr(null);
    setJob(null);
    try {
      const body = {
        serial: serial.trim().toUpperCase(),
        district_name: district.trim(),
        school_name:   school.trim(),
        grade_band:    band,
        grades: grades.split(",").map((g) => g.trim()).filter(Boolean),
        password: password || null,
        strict,
        dry_run: dryRun,
      };
      const { data } = await axios.post(`${API}/api/qc-usb/mint`, body);
      setJob({ job_id: data.job_id, status: "pending",
               phase: "queued", serial: data.serial });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSubmitErr(typeof d === "string" ? d
                    : d?.detail || d?.reasons?.join(", ") || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Poll job status every 2s until done/failed.
  useEffect(() => {
    if (!job?.job_id || job.status === "done" || job.status === "failed") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/qc-usb/mint-jobs/${job.job_id}`);
        if (cancelled) return;
        setJob(data);
        if (data.status === "done" && onMinted) {
          onMinted(data.serial);
        }
      } catch { /* keep polling */ }
    };
    tick();
    const iv = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [job?.job_id, job?.status, onMinted]);

  const canSubmit = !submitting
    && serial.trim().length >= 12
    && district.trim() && school.trim()
    && (password === "" || policy?.ok);

  return (
    <HubSection
      title="Mint a new drive"
      subtitle="Build a sealed USB image from the browser — no CLI. All 47 pytest contracts + 36 verifier invariants run automatically when Strict Mode is on."
      testId="hub-mint-section"
    >
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="hub-mint-open-btn"
          style={{
            padding: "10px 16px",
            background: "#1D4ED8",
            color: "#FFFFFF",
            border: "1px solid #1D4ED8",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          + Mint drive
        </button>
      )}
      {open && (
        <div data-testid="hub-mint-form" style={{ display: "grid", gap: 14 }}>
          {/* Row 1: serial + band */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div>
              <MintFieldLabel hint="MOS-USB-<band>-<code>, e.g. MOS-USB-K8-ABC001">Serial</MintFieldLabel>
              <input
                data-testid="mint-serial"
                value={serial}
                onChange={(e) => setSerial(e.target.value.toUpperCase())}
                placeholder="MOS-USB-K8-ABC001"
                style={{ ...MintInputStyle, fontFamily: "ui-monospace, SF Mono, Menlo, monospace" }}
              />
            </div>
            <div>
              <MintFieldLabel>Grade band</MintFieldLabel>
              <select
                data-testid="mint-band"
                value={band}
                onChange={(e) => applyBand(e.target.value)}
                style={MintInputStyle}
              >
                {["K-2", "K-5", "3-5", "6-8", "9-12", "K-8", "K-12"].map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: district + school */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <MintFieldLabel>District name</MintFieldLabel>
              <input
                data-testid="mint-district"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                placeholder="NYC · District 12"
                style={MintInputStyle}
              />
            </div>
            <div>
              <MintFieldLabel>School name</MintFieldLabel>
              <input
                data-testid="mint-school"
                value={school}
                onChange={(e) => setSchool(e.target.value)}
                placeholder="PS 042 · Claremont"
                style={MintInputStyle}
              />
            </div>
          </div>

          {/* Row 3: grades */}
          <div>
            <MintFieldLabel hint="Comma-separated, updates automatically when you pick a band">Grades</MintFieldLabel>
            <input
              data-testid="mint-grades"
              value={grades}
              onChange={(e) => setGrades(e.target.value)}
              style={{ ...MintInputStyle, fontFamily: "ui-monospace, SF Mono, Menlo, monospace" }}
            />
          </div>

          {/* Row 4: password */}
          <div>
            <MintFieldLabel hint="Leave empty for open QC / demo drives · 12+ chars · 3-of-4 classes">Password (AES-256-GCM at rest)</MintFieldLabel>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                data-testid="mint-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="(leave empty for QC · demo drive)"
                style={{ ...MintInputStyle, flex: 1, fontFamily: password ? "ui-monospace, SF Mono, Menlo, monospace" : "inherit" }}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                data-testid="mint-password-show-btn"
                style={{
                  padding: "0 12px", background: "#FFFFFF",
                  border: "1px solid #CBD5E1", borderRadius: 6,
                  fontSize: 12, cursor: "pointer",
                }}
              >
                {showPw ? "Hide" : "Show"}
              </button>
              <button
                type="button"
                onClick={suggestPassword}
                data-testid="mint-password-suggest-btn"
                style={{
                  padding: "0 12px", background: "#FFFFFF",
                  border: "1px solid #CBD5E1", borderRadius: 6,
                  fontSize: 12, cursor: "pointer", fontWeight: 600,
                }}
              >
                🎲 Generate
              </button>
            </div>
            {password && policy && (
              <StrengthMeter
                score={policy.strength_score}
                reasons={policy.reasons}
              />
            )}
          </div>

          {/* Row 5: options */}
          <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={strict}
                onChange={(e) => setStrict(e.target.checked)}
                data-testid="mint-strict"
              />
              Strict mode <span style={{ color: "#64748B" }}>(preflight + 36 verifier invariants)</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                data-testid="mint-dryrun"
              />
              Dry run <span style={{ color: "#64748B" }}>(skip the tarball, staging tree only)</span>
            </label>
          </div>

          {/* Submit */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="mint-submit-btn"
              style={{
                padding: "10px 18px",
                background: canSubmit ? "#14804A" : "#94A3B8",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: canSubmit ? "pointer" : "not-allowed",
                letterSpacing: ".02em",
              }}
            >
              {submitting ? "Starting…" : password
                ? "🔒 Mint gated drive"
                : "○ Mint open drive"}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setJob(null); }}
              data-testid="mint-cancel-btn"
              style={{
                padding: "10px 14px",
                background: "#FFFFFF",
                color: "#0F172A",
                border: "1px solid #CBD5E1",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            {password && (
              <span style={{ fontSize: 11, color: "#64748B" }}>
                {" · "} Remember to record this password on the retail
                sticker BEFORE clicking Mint — it never leaves the browser.
              </span>
            )}
          </div>

          {submitErr && (
            <div
              data-testid="mint-error"
              style={{
                padding: "10px 14px", background: "#FEE4E2",
                color: "#B42318", borderRadius: 8, fontSize: 13,
              }}
            >
              {submitErr}
            </div>
          )}

          {job && (
            <div
              data-testid="mint-job-status"
              style={{
                marginTop: 4,
                padding: "14px 16px",
                background: job.status === "done"    ? "#E6F4EC"
                          : job.status === "failed" ? "#FEE4E2"
                          : "#F1F5F9",
                borderLeft: `4px solid ${
                  job.status === "done"    ? "#14804A"
                : job.status === "failed" ? "#B42318"
                : "#1D4ED8"}`,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <div>
                  <b>{job.serial}</b>{" · "}
                  <span style={{
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    fontSize: 11, color: "#475569",
                  }}>
                    job {job.job_id}
                  </span>
                </div>
                <div style={{ fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase" }}>
                  {job.status === "done"    ? "✓ Ready" :
                   job.status === "failed" ? "✕ Failed" :
                   job.status === "running" ? "⟳ Building…" : "· Queued"}
                </div>
              </div>
              {job.error && (
                <div style={{ marginTop: 6, color: "#B42318" }}>{job.error}</div>
              )}
              {job.log_tail && (
                <pre
                  data-testid="mint-job-log"
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    background: "#0F172A",
                    color: "#E2E8F0",
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    fontSize: 11,
                    lineHeight: 1.5,
                    borderRadius: 6,
                    maxHeight: 240,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {job.log_tail.split("\n").slice(-40).join("\n")}
                </pre>
              )}
              {job.status === "done" && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#065F46" }}>
                  Drive is ready — the "Grab the drive" section auto-selected
                  it and 30-day signed URLs are minted below.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </HubSection>
  );
};

const SealedUsbHub = () => {
  const [drives, setDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null); // serial
  const [links, setLinks] = useState(null);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksErr, setLinksErr] = useState(null);
  // v6.43.5 — re-verify in-flight state, keyed by serial so multiple
  // drives can be re-verified in parallel without stepping on each other.
  const [verifying, setVerifying] = useState({});
  const [verifyErr, setVerifyErr] = useState(null);

  // Fetch mint history on mount.
  const reloadDrives = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/api/qc-usb/list`);
      setDrives(data.drives || []);
      return data.drives || [];
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load drives");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const rows = await reloadDrives();
      if (cancelled) return;
      const latest = rows[0];
      if (latest) setSelected(latest.serial);
    })();
    return () => { cancelled = true; };
  }, []);

  const reVerify = async (serial) => {
    setVerifying((s) => ({ ...s, [serial]: true }));
    setVerifyErr(null);
    try {
      await axios.post(`${API}/api/qc-usb/${serial}/verify`);
      await reloadDrives();
    } catch (e) {
      setVerifyErr(
        e?.response?.data?.detail || e.message || `Re-verify failed for ${serial}`
      );
    } finally {
      setVerifying((s) => ({ ...s, [serial]: false }));
    }
  };

  // Fetch signed URLs whenever the selected drive changes.
  useEffect(() => {
    if (!selected) { setLinks(null); return; }
    let cancelled = false;
    (async () => {
      setLinksLoading(true);
      setLinksErr(null);
      try {
        const { data } = await axios.get(
          `${API}/api/qc-usb/${selected}/mint-links?ttl_days=30`
        );
        if (!cancelled) setLinks(data);
      } catch (e) {
        if (cancelled) return;
        setLinksErr(e?.response?.data?.detail || e.message || "Failed to mint links");
      } finally {
        if (!cancelled) setLinksLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const selectedDrive = useMemo(
    () => drives.find((d) => d.serial === selected),
    [drives, selected]
  );

  return (
    <div
      data-testid="sealed-usb-hub-page"
      style={{ padding: "28px 20px", background: "#F7F8FB", minHeight: "100vh" }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>

        {/* ─── HEADER ─── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, letterSpacing: "-.01em" }}>
                Sealed MOSAIC USB · Hub
              </h1>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#475569", maxWidth: 640 }}>
                One door for everything USB — grab the latest QC image, get flash guides
                for any OS, download the retail box insert, and see the full mint history.
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Link
                to="/admin"
                data-testid="hub-back-to-admin"
                style={{
                  padding: "9px 14px",
                  background: "#FFFFFF",
                  border: "1px solid #CBD5E1",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#0F172A",
                  textDecoration: "none",
                }}
              >
                ← Super Admin
              </Link>
              <Link
                to="/admin/sealed-usb-compatibility"
                data-testid="hub-compat-link"
                style={{
                  padding: "9px 14px",
                  background: "#1D4ED8",
                  color: "#FFFFFF",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Compatibility ↗
              </Link>
            </div>
          </div>
        </div>

        {/* ─── SECTION 0: USB ORDERS FULFILLMENT QUEUE ─── */}
        {/* v6.45.0 — Paid orders flow in here from accepted quotes.    */}
        {/* Ops reviews, authorizes, and launches the build pipeline    */}
        {/* with one click. All serial allocation, password handling,   */}
        {/* and verifier/pytest safety nets run under the hood.         */}
        <UsbOrdersPanel />

        {/* ─── SECTION 0b: MINT A NEW DRIVE (standalone, no order) ─── */}
        <MintDrivePanel
          onMinted={async (newSerial) => {
            const rows = await reloadDrives();
            if (rows.find((r) => r.serial === newSerial)) {
              setSelected(newSerial);
            }
          }}
        />

        {/* ─── SECTION 1: LATEST / SELECTED DRIVE + SIGNED LINKS ─── */}
        <HubSection
          title="Grab the drive"
          subtitle={
            selectedDrive
              ? `30-day signed URLs for ${selectedDrive.serial}. Paste anywhere — chat, email, SMS. Clicks flow through auth automatically.`
              : "Select a drive below to mint download links."
          }
          testId="hub-grab-section"
        >
          {loading && (
            <div style={{ padding: "18px 0", color: "#64748B", fontSize: 13 }}>Loading drives…</div>
          )}
          {err && (
            <div
              data-testid="hub-drives-error"
              style={{
                padding: "12px 14px",
                background: "#FEE4E2",
                color: "#B42318",
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              {err}
            </div>
          )}
          {!loading && !err && drives.length === 0 && (
            <div
              data-testid="hub-drives-empty"
              style={{
                padding: "18px 16px",
                background: "#FFFBEB",
                borderLeft: "3px solid #F59E0B",
                fontSize: 13,
                color: "#78350F",
                borderRadius: 4,
              }}
            >
              No drives currently staged on this server. Run the mintage script to build one:
              <code
                style={{
                  display: "block",
                  marginTop: 6,
                  padding: "8px 10px",
                  background: "#1E293B",
                  color: "#F1F5F9",
                  borderRadius: 4,
                  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                  fontSize: 11,
                }}
              >
                python backend/scripts/build_usb_image.py --serial MOS-USB-K8-QC001 …
              </code>
            </div>
          )}
          {selectedDrive && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 14px",
                  background: "#EEF2FF",
                  border: "1px solid #C7D2FE",
                  borderRadius: 8,
                  marginBottom: 14,
                  fontSize: 13,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <div>
                  <b>{selectedDrive.serial}</b>
                  <span style={{ color: "#475569", marginLeft: 10 }}>
                    band {selectedDrive.band || "—"}
                    {selectedDrive.build_version && ` · build ${selectedDrive.build_version}`}
                    {" · built "}
                    {fmtWhen(selectedDrive.built_utc)}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <VerifierPill
                    v={selectedDrive.verifier}
                    testId={`hub-selected-verifier-pill-${selectedDrive.serial}`}
                  />
                  <PasswordPill
                    gated={selectedDrive.password_gated}
                    fingerprint={selectedDrive.password_fingerprint}
                    testId={`hub-selected-password-pill-${selectedDrive.serial}`}
                  />
                  <button
                    type="button"
                    onClick={() => reVerify(selectedDrive.serial)}
                    disabled={!!verifying[selectedDrive.serial]}
                    data-testid={`hub-reverify-btn-${selectedDrive.serial}`}
                    style={{
                      padding: "4px 10px",
                      background: "#FFFFFF",
                      color: "#0F172A",
                      border: "1px solid #CBD5E1",
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: verifying[selectedDrive.serial] ? "wait" : "pointer",
                      opacity: verifying[selectedDrive.serial] ? 0.6 : 1,
                    }}
                  >
                    {verifying[selectedDrive.serial] ? "Verifying…" : "Re-verify"}
                  </button>
                  {selectedDrive.is_qc && (
                    <span
                      style={{
                        padding: "3px 10px",
                        background: "#FCD34D",
                        color: "#78350F",
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: ".05em",
                      }}
                    >
                      QC
                    </span>
                  )}
                </div>
              </div>

              {linksLoading && (
                <div style={{ padding: "10px 0", color: "#64748B", fontSize: 13 }}>Minting signed URLs…</div>
              )}
              {linksErr && (
                <div
                  style={{
                    padding: "12px 14px",
                    background: "#FEE4E2",
                    color: "#B42318",
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  {linksErr}
                </div>
              )}
              {links && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {selectedDrive.has_tarball && (
                    <SignedLinkRow
                      label="Full drive (tar.gz)"
                      href={links.links.tarball}
                      filename={`${selectedDrive.serial}.tar.gz`}
                      size={fmtBytes(selectedDrive.tarball_bytes)}
                      testId="hub-link-tarball"
                    />
                  )}
                  {selectedDrive.has_flashable_img && (
                    <SignedLinkRow
                      label="Flashable disk image (img.gz)"
                      href={links.links.flashable_img}
                      filename={`${selectedDrive.serial}.img.gz`}
                      size={fmtBytes(selectedDrive.flashable_img_bytes)}
                      testId="hub-link-flashable"
                    />
                  )}
                  {!selectedDrive.has_tarball && !selectedDrive.has_flashable_img && (
                    <div
                      data-testid="hub-drive-no-artifacts"
                      style={{
                        padding: "12px 14px",
                        background: "#FEF3C7",
                        color: "#78350F",
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    >
                      This drive is staged but no <code>.tar.gz</code> or <code>.img.gz</code> is archived
                      on disk yet — re-run the mint script to produce shippable artifacts.
                    </div>
                  )}
                  <SignedLinkRow
                    label="Retail box insert (PDF, 4-up)"
                    href={links.links.retail_insert}
                    filename={`${selectedDrive.serial}_retail_insert.pdf`}
                    testId="hub-link-retail-insert"
                  />
                  <SignedLinkRow
                    label="SHA-256 checksum"
                    href={links.links.sha256}
                    filename={`${selectedDrive.serial}.tar.gz.sha256`}
                    testId="hub-link-sha256"
                  />
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: "#64748B",
                    }}
                  >
                    Links expire{" "}
                    <b>
                      {fmtWhen(links.expires_utc)}
                    </b>
                    {" · signed with HMAC-SHA256 tied to MONGO_URL (rotating the DB invalidates every link)."}
                  </div>
                </div>
              )}
            </div>
          )}
        </HubSection>

        {/* ─── SECTION 2: MINT HISTORY ─── */}
        <HubSection
          title="Mint history"
          subtitle="Every drive currently staged on this server. Click a row to switch which drive the links above point at. Each row's verify pill reflects the last 30-invariant sweep — click ↻ to re-run."
          testId="hub-history-section"
        >
          {verifyErr && (
            <div
              data-testid="hub-verify-error"
              style={{
                padding: "10px 14px",
                background: "#FEE4E2",
                color: "#B42318",
                borderRadius: 8,
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {verifyErr}
            </div>
          )}
          {!loading && drives.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table
                data-testid="hub-history-table"
                style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
              >
                <thead>
                  <tr>
                    {["Serial", "Band", "Built", "Build", "tar.gz", "img.gz", "Verify", ""].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: ".08em",
                          color: "#64748B",
                          borderBottom: "1px solid #E4E7EC",
                          background: "#FAFBFC",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drives.map((d) => {
                    const isActive = d.serial === selected;
                    return (
                      <tr
                        key={d.serial}
                        data-testid={`hub-history-row-${d.serial}`}
                        onClick={() => setSelected(d.serial)}
                        style={{
                          cursor: "pointer",
                          background: isActive ? "#EEF2FF" : "transparent",
                        }}
                      >
                        <td style={{ padding: "12px", borderBottom: "1px solid #F1F5F9", fontWeight: isActive ? 700 : 500 }}>
                          {d.serial}
                          {d.is_qc && (
                            <span
                              style={{
                                marginLeft: 8,
                                padding: "1px 7px",
                                background: "#FCD34D",
                                color: "#78350F",
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 700,
                              }}
                            >
                              QC
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid #F1F5F9" }}>{d.band || "—"}</td>
                        <td style={{ padding: "12px", borderBottom: "1px solid #F1F5F9", color: "#64748B" }}>{fmtWhen(d.built_utc)}</td>
                        <td style={{ padding: "12px", borderBottom: "1px solid #F1F5F9", color: "#64748B" }}>{d.build_version || "—"}</td>
                        <td style={{ padding: "12px", borderBottom: "1px solid #F1F5F9", color: d.has_tarball ? "#0F172A" : "#94A3B8" }}>
                          {d.has_tarball ? fmtBytes(d.tarball_bytes) : "—"}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid #F1F5F9", color: d.has_flashable_img ? "#0F172A" : "#94A3B8" }}>
                          {d.has_flashable_img ? fmtBytes(d.flashable_img_bytes) : "—"}
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid #F1F5F9" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <VerifierPill
                              v={d.verifier}
                              size="sm"
                              testId={`hub-verifier-pill-${d.serial}`}
                            />
                            <PasswordPill
                              gated={d.password_gated}
                              fingerprint={d.password_fingerprint}
                              testId={`hub-password-pill-${d.serial}`}
                            />
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); reVerify(d.serial); }}
                              disabled={!!verifying[d.serial]}
                              data-testid={`hub-history-reverify-${d.serial}`}
                              style={{
                                padding: "2px 8px",
                                background: "transparent",
                                color: "#475569",
                                border: "1px solid #E4E7EC",
                                borderRadius: 4,
                                fontSize: 10,
                                fontWeight: 600,
                                cursor: verifying[d.serial] ? "wait" : "pointer",
                                opacity: verifying[d.serial] ? 0.6 : 1,
                              }}
                            >
                              {verifying[d.serial] ? "…" : "↻"}
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: "12px", borderBottom: "1px solid #F1F5F9", textAlign: "right" }}>
                          {isActive ? (
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#4338CA", letterSpacing: ".05em" }}>SELECTED</span>
                          ) : (
                            <span style={{ fontSize: 11, color: "#64748B" }}>Select →</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </HubSection>

        {/* ─── SECTION 3: FLASH GUIDES ─── */}
        <HubSection
          title="Flash it"
          subtitle="Step-by-step for every OS. Everything below works offline — pin this tab during a school-site install."
          testId="hub-flash-section"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 14,
            }}
          >
            {FLASH_GUIDES.map((g) => (
              <div
                key={g.os}
                data-testid={`hub-flash-${g.os.toLowerCase()}`}
                style={{
                  background: g.tint,
                  border: "1px solid #E4E7EC",
                  borderRadius: 10,
                  padding: "16px 18px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 22 }}>{g.icon}</span>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{g.os}</div>
                </div>
                <div style={{ fontSize: 11, color: "#64748B", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".05em" }}>
                  {g.tool}
                </div>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#0F172A", lineHeight: 1.55 }}>
                  {g.steps.map((s, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      <span
                        style={{
                          fontFamily: s.match(/[/-]|sudo |diskutil|gunzip|lsblk/) ? "ui-monospace, SF Mono, Menlo, monospace" : "inherit",
                          fontSize: s.match(/[/-]|sudo |diskutil|gunzip|lsblk/) ? 11.5 : "inherit",
                        }}
                      >
                        {s}
                      </span>
                    </li>
                  ))}
                </ol>
                {g.warning && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "8px 10px",
                      background: "#FFFBEB",
                      borderLeft: "3px solid #F59E0B",
                      fontSize: 11,
                      color: "#78350F",
                      lineHeight: 1.4,
                    }}
                  >
                    ⚠ {g.warning}
                  </div>
                )}
              </div>
            ))}
          </div>
        </HubSection>

        {/* ─── SECTION 3.5: RETAIL PRINTS LIBRARY ─── */}
        {/* v6.45.5 — Centralized print room. Lives at the hub level so
            ops never has to open an order drawer just to reach the 4
            retail inserts. Mirrors the per-drive card and the Pack
            step action card — same 4 polished PDFs, same brand palette. */}
        <HubSection
          title="Retail Prints Library"
          subtitle="The 4 polished PDFs that ship in every retail box — print on demand, no order drawer needed."
          testId="hub-retail-prints-section"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 12,
            }}
          >
            {[
              { file: "MOSAIC_ThankYou_Letter.pdf",      icon: "💌", label: "Thank You Letter",        sub: "Welcome & gratitude · signed · page 1 of box",            testId: "retail-hub-thank-you" },
              { file: "MOSAIC_QuickStart_Retail.pdf",    icon: "🚀", label: "Quick Start Card",         sub: "One page · 4 numbered steps · cardstock",                 testId: "retail-hub-quickstart" },
              { file: "MOSAIC_UserManual_Retail.pdf",    icon: "📘", label: "User Manual",              sub: "Multi-page · section-tabbed · booklet",                   testId: "retail-hub-manual" },
              { file: "MOSAIC_PreInsert_Checklist.pdf",  icon: "☑️", label: "Pre-Insertion Checklist",  sub: "One page · tick-box · 2-min pre-flight",                  testId: "retail-hub-checklist" },
            ].map((p) => (
              <a
                key={p.file}
                href={`/retail_box/${p.file}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={p.testId}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "14px 16px",
                  background: "#FFFBEB",
                  border: "1px solid #FCD34D",
                  borderRadius: 10,
                  textDecoration: "none",
                  color: "inherit",
                  transition: "background .12s ease, transform .12s ease",
                }}
              >
                <span style={{ fontSize: 24, flexShrink: 0 }}>{p.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
                    {p.label}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                    {p.sub}
                  </div>
                </span>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: "#92400E",
                  padding: "4px 8px", background: "#FDE68A", borderRadius: 4,
                  flexShrink: 0,
                }}>PRINT →</span>
              </a>
            ))}
          </div>
          <div style={{
            marginTop: 12, padding: 10,
            background: "#F1F5F9", borderRadius: 6,
            fontSize: 11, color: "#475569",
          }}>
            💡 Also available inside each order drawer (per-drive card, and
            Step 6 — Pack action card). All 4 PDFs share the same brand:
            deep MOSAIC indigo + warm cream + gold accent. Print on 65-lb
            cardstock for the inserts; staple the User Manual as a booklet.
          </div>
        </HubSection>

        {/* ─── SECTION 4: QUICK LINKS ─── */}
        <HubSection
          title="Quick links"
          subtitle="Compatibility matrix, corpus health, and the offline scoring preview — all one click away."
          testId="hub-quick-section"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {[
              { to: "/admin/sealed-usb-compatibility", label: "Compatibility matrix", desc: "Live device probe + hand-tested browser grid.", testId: "quick-compat" },
              { to: "/admin/corpus-health", label: "Corpus health", desc: "Grade × standard × form coverage + trend.", testId: "quick-corpus" },
              { to: "/usb-preview/scoring/?demo=1&rep=qc_k8", label: "Offline preview (QC K-8)", desc: "Open the sealed scoring app in a new tab.", testId: "quick-preview", external: true },
            ].map((tile) => {
              const inner = (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{tile.label}</div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 4, lineHeight: 1.4 }}>{tile.desc}</div>
                </div>
              );
              const style = {
                display: "block",
                padding: "14px 16px",
                background: "#FAFBFC",
                border: "1px solid #E4E7EC",
                borderRadius: 10,
                textDecoration: "none",
                color: "inherit",
                transition: "background .12s ease",
              };
              if (tile.external) {
                return (
                  <a key={tile.to} href={tile.to} target="_blank" rel="noopener noreferrer" style={style} data-testid={tile.testId}>
                    {inner}
                  </a>
                );
              }
              return (
                <Link key={tile.to} to={tile.to} style={style} data-testid={tile.testId}>
                  {inner}
                </Link>
              );
            })}
          </div>
        </HubSection>

        <p style={{ textAlign: "center", fontSize: 11, color: "#94A3B8", marginTop: 24 }}>
          MOSAIC · Sealed USB Hub · v6.43.4
        </p>
      </div>
    </div>
  );
};

export default SealedUsbHub;
